import React, { useMemo, useRef, useState } from 'react';
import PptxStylePicker, { isPptxStyleQuestion } from './PptxStylePicker';
import FollowupQuestionForm from '../common/FollowupQuestionForm';
import type { CoworkPermissionRequest, CoworkPermissionResult } from '../../types/cowork';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { i18nService } from '../../services/i18n';

interface CoworkInlinePermissionProps {
  permission: CoworkPermissionRequest;
  onRespond: (result: CoworkPermissionResult) => void | Promise<void>;
}

type QuestionOption = {
  label: string;
  description?: string;
};

type QuestionItem = {
  question: string;
  header?: string;
  options: QuestionOption[];
  multiSelect?: boolean;
};


const CoworkInlinePermission: React.FC<CoworkInlinePermissionProps> = ({
  permission,
  onRespond,
}) => {
  const submitting = useRef(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const toolInput = permission.toolInput ?? {};

  // Parse questions for AskUserQuestion tool
  const questions = useMemo<QuestionItem[]>(() => {
    if (permission.toolName !== 'AskUserQuestion') return [];
    if (!toolInput || typeof toolInput !== 'object') return [];
    const rawQuestions = (toolInput as Record<string, unknown>).questions;
    if (!Array.isArray(rawQuestions)) return [];

    return rawQuestions
      .map((question) => {
        if (!question || typeof question !== 'object') return null;
        const record = question as Record<string, unknown>;
        const options = Array.isArray(record.options)
          ? record.options
              .map((option) => {
                if (!option || typeof option !== 'object') return null;
                const optionRecord = option as Record<string, unknown>;
                if (typeof optionRecord.label !== 'string') return null;
                return {
                  label: optionRecord.label,
                  description: typeof optionRecord.description === 'string'
                    ? optionRecord.description
                    : undefined,
                } as QuestionOption;
              })
              .filter(Boolean) as QuestionOption[]
          : [];

        if (typeof record.question !== 'string' || options.length === 0) {
          return null;
        }

        return {
          question: record.question,
          header: typeof record.header === 'string' ? record.header : undefined,
          options,
          multiSelect: Boolean(record.multiSelect),
        } as QuestionItem;
      })
      .filter(Boolean) as QuestionItem[];
  }, [permission.toolName, toolInput]);

  const isQuestionTool = questions.length > 0;


  const formatToolInput = (input: Record<string, unknown>): string => {
    try {
      return JSON.stringify(input, null, 2);
    } catch {
      return String(input);
    }
  };

  const isDangerousBash = (() => {
    if (permission.toolName !== 'Bash') return false;
    const command = String((permission.toolInput as Record<string, unknown>)?.command ?? '');
    const dangerousPatterns = [
      /\brm\s+-rf?\b/i,
      /\bsudo\b/i,
      /\bdd\b/i,
      /\bmkfs\b/i,
      /\bformat\b/i,
      />\s*\/dev\//i,
    ];
    return dangerousPatterns.some(pattern => pattern.test(command));
  })();
  /** 常规工具审批仍使用原协议，追问答案由共用表单处理。 */
  const respond = async (result: CoworkPermissionResult) => {
    if (submitting.current) return;
    submitting.current = true; setBusy(true); setError('');
    try { await onRespond(result); }
    catch (cause) { setError(cause instanceof Error ? cause.message : String(cause)); }
    finally { submitting.current = false; setBusy(false); }
  };
  const handleApprove = () => void respond({ behavior: 'allow', updatedInput: toolInput });

  const handleDeny = () => {
    void respond({ behavior: 'deny', message: 'Permission denied' });
  };


  const denyLabel = isQuestionTool
    ? i18nService.t('coworkDenyRequest')
    : i18nService.t('coworkDeny');
  const approveLabel = isQuestionTool
    ? i18nService.t('coworkConfirmSelection')
    : i18nService.t('coworkApprove');

  if (isQuestionTool && questions.length === 1 && !questions[0].multiSelect && isPptxStyleQuestion(questions[0])) {
    return <PptxStylePicker key={permission.requestId} question={questions[0]} language={i18nService.getLanguage()}
      onSubmit={answers => onRespond({ behavior: 'allow', updatedInput: { ...toolInput, answers } })}
      onDeny={() => onRespond({ behavior: 'deny', message: 'Permission denied' })} />;
  }

  if (isQuestionTool) {
    return <FollowupQuestionForm key={permission.requestId} initialAnswers={toolInput.answers} questions={questions} language={i18nService.getLanguage()}
      onSubmit={(answers) => onRespond({ behavior: 'allow', updatedInput: { ...toolInput, answers } })}
      onDeny={() => onRespond({ behavior: 'deny', message: 'Permission denied' })} />;
  }

  return (
    <div className="followup-body rounded-xl border dark:border-yellow-700/40 border-yellow-300 dark:bg-claude-darkSurface bg-claude-surface shadow-sm overflow-hidden">
      {/* 权限请求由 Desktop 会话生命周期管理，Web 仅负责展示和响应。 */}
      <div className="flex items-center gap-3 px-4 py-3 border-b dark:border-claude-darkBorder border-claude-border">
        <div className="p-1.5 rounded-full bg-yellow-100 dark:bg-yellow-900/30">
          <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 dark:text-yellow-500" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold dark:text-claude-darkText text-claude-text">
            {i18nService.t('coworkPermissionRequired')}
          </h3>
          <p className="text-xs dark:text-claude-darkTextSecondary text-claude-textSecondary truncate">
            {i18nService.t('coworkPermissionDescription')}
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="px-4 py-3 space-y-3 overflow-y-auto">
        {(
          <>
            {/* Tool name */}
            <div>
              <label className="block text-xs font-medium dark:text-claude-darkTextSecondary text-claude-textSecondary uppercase tracking-wider mb-1">
                {i18nService.t('coworkToolName')}
              </label>
              <div className="px-3 py-2 rounded-lg dark:bg-claude-darkBg bg-claude-bg">
                <code className="text-sm dark:text-claude-darkText text-claude-text">
                  {permission.toolName}
                </code>
              </div>
            </div>

            {/* Tool input */}
            <div>
              <label className="block text-xs font-medium dark:text-claude-darkTextSecondary text-claude-textSecondary uppercase tracking-wider mb-1">
                {i18nService.t('coworkToolInput')}
              </label>
              <div className="px-3 py-2 rounded-lg dark:bg-claude-darkBg bg-claude-bg max-h-40 overflow-y-auto">
                <pre className="text-xs dark:text-claude-darkText text-claude-text whitespace-pre-wrap break-words font-mono">
                  {formatToolInput(permission.toolInput)}
                </pre>
              </div>
            </div>

            {/* Dangerous bash warning */}
            {isDangerousBash && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
                <ExclamationTriangleIcon className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700 dark:text-red-400">
                  {i18nService.t('coworkDangerousOperation')}
                </p>
              </div>
            )}
          </>
        )}
      </div>

      {error && <p role="alert" className="px-4 text-sm text-red-600">{error}</p>}
      {/* Footer */}
      <div className="flex items-center justify-end gap-3 px-4 py-3 border-t dark:border-claude-darkBorder border-claude-border">
        <button
          disabled={busy}
          onClick={handleDeny}
          className="px-4 py-1.5 text-sm font-medium rounded-lg dark:text-claude-darkTextSecondary text-claude-textSecondary dark:hover:bg-claude-darkSurfaceHover hover:bg-claude-surfaceHover transition-colors"
        >
          {denyLabel}
        </button>
        <button
          disabled={busy}
          onClick={handleApprove}
          className="px-4 py-1.5 text-sm font-medium rounded-lg bg-claude-accent hover:bg-claude-accentHover text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {approveLabel}
        </button>
      </div>
    </div>
  );
};

export default CoworkInlinePermission;
